/**
 * 同步管理器
 * - 协调 WS 连接 + 离线队列 + 数据刷新
 * - 监听 token 变化自动连接/断开
 * - 收到 change 消息触发全局 refresh 事件
 * - 重连后拉增量 + 重放队列
 */

import { useAuthStore } from '../store/auth';
import { useSyncStore } from './store';
import { syncSocket, type SyncMessage } from './socket';
import { offlineQueue } from './queue';
import { api, syncApi, ApiError } from '../api/client';
import { useReminderStore } from '../store/reminder';

const LAST_SYNC_KEY = 'todo_last_sync';

export function initSync(): void {
  // 1. token 变化 → 连接/断开
  useAuthStore.subscribe((state, prev) => {
    if (state.token !== prev.token) {
      if (state.token) {
        syncSocket.connect(state.token);
      } else {
        syncSocket.disconnect();
      }
    }
  });

  // 2. socket 状态变化 → 更新 store + 触发同步
  syncSocket.onState((s) => {
    useSyncStore.getState().setSocketState(s);
    if (s === 'connected') {
      doSync();
    }
  });

  // 3. 收到 change 消息 → 广播给前端组件
  syncSocket.onMessage((msg) => {
    if (msg.type === 'change') {
      // reminder 触发的 update: 推进提醒队列, 由 ReminderDialog 渲染弹窗
      if (msg.reminder) {
        const r = msg.reminder;
        useReminderStore.getState().push({
          id: `${r.task_id}-${r.kind}-${msg.ts}`,
          task_id: r.task_id,
          title: r.title,
          body: r.body,
          kind: r.kind,
          urgency: r.urgency,
          receivedAt: msg.ts,
        });
        // 切回标签时也能看到提示
        const original = document.title;
        document.title = `⏰ ${r.title} — Todo`;
        setTimeout(() => {
          if (document.title.startsWith('⏰ ')) document.title = original;
        }, 30_000);
      }
      window.dispatchEvent(
        new CustomEvent<SyncMessage>('todo:change', { detail: msg })
      );
      useSyncStore.getState().markSynced();
    }
    if (msg.type === 'kicked') {
      // 被踢, 清 token
      useAuthStore.getState().clear();
    }
  });

  // 4. 浏览器网络状态变化 (额外保险)
  window.addEventListener('online', () => {
    const t = useAuthStore.getState().token;
    if (t && syncSocket.getState() !== 'connected') {
      syncSocket.connect(t);
    }
  });
  window.addEventListener('offline', () => {
    useSyncStore.getState().setSocketState('offline');
  });

  // 5. 启动时如果有 token, 连接
  const t = useAuthStore.getState().token;
  if (t) syncSocket.connect(t);

  // 6. 初始化队列大小 + 监听 queue 变化
  useSyncStore.getState().setQueueSize(offlineQueue.size());
  window.addEventListener('todo:queue-changed', (e) => {
    useSyncStore.getState().setQueueSize((e as CustomEvent<number>).detail);
  });

  // 7. 监听 api 层的写操作离线事件 → 入队
  window.addEventListener('todo:write-offline', (e) => {
    const { method, path, body } = (e as CustomEvent<{
      method: 'POST' | 'PATCH' | 'DELETE';
      path: string;
      body: unknown;
    }>).detail;
    offlineQueue.enqueue(method, path, body);
  });
}

let syncing = false;
async function doSync(): Promise<void> {
  if (syncing) return;
  syncing = true;
  try {
    // 1. 拉增量
    const lastSync = parseInt(localStorage.getItem(LAST_SYNC_KEY) ?? '0', 10);
    try {
      const snap = await syncApi.snapshot(lastSync);
      // 通知 hooks (他们决定怎么 merge, 现在简单: 触发 refresh 事件)
      window.dispatchEvent(new CustomEvent('todo:snapshot', { detail: snap }));
      localStorage.setItem(LAST_SYNC_KEY, String(snap.server_time));
      useSyncStore.getState().markSynced();
    } catch (err) {
      console.warn('[sync] snapshot failed:', err);
    }

    // 2. 重放队列
    useSyncStore.getState().setQueueSize(offlineQueue.size());
    const r = await offlineQueue.replay(async (item) => {
      try {
        await api(item.path, {
          method: item.method,
          body: item.body !== undefined ? JSON.stringify(item.body) : undefined,
        });
        return { ok: true, shouldRetry: false };
      } catch (err) {
        if (err instanceof ApiError) {
          if (err.status >= 400 && err.status < 500) {
            // 业务错误, 不重试
            return { ok: false, shouldRetry: false };
          }
        }
        // 网络/5xx 错误, 重试
        return { ok: false, shouldRetry: true };
      }
    });
    if (r.ok > 0 || r.failed > 0) {
      console.log(`[sync] replayed ${r.ok}, dropped ${r.failed}`);
    }
    useSyncStore.getState().setQueueSize(offlineQueue.size());
  } finally {
    syncing = false;
  }
}